import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentBox } from "@msuite/ui/content-box";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Tabs } from "@msuite/ui/tabs";
import { Navigate, useParams } from "react-router";
import { Can } from "@/components/citadel";
import { useIncomingInvoice } from "@/hooks/use-incoming-invoice";
import { IncomingInvoiceHeader } from "./incoming-invoice-header";
import { IncomingInvoiceCostAllocation } from "./panels/incoming-invoice-cost-allocation";
import { IncomingInvoiceGeneral } from "./panels/incoming-invoice-general";
import { IncomingInvoicePositions } from "./panels/incoming-invoice-positions";
import { IncomingInvoiceReview } from "./panels/incoming-invoice-review";
import { IncomingInvoiceDetailsTab } from "./panels/incoming-invoice-details-tab";
import { IncomingInvoiceSettings } from "./panels/incoming-invoice-settings";

export function IncomingInvoice() {
	/** Hooks */
	const params = useParams<{ incomingInvoiceId: string }>();
	const { t } = useTranslation();
	const incomingInvoiceId = params.incomingInvoiceId;

	/** Query */
	const { incomingInvoice } = useIncomingInvoice(incomingInvoiceId ?? "");

	/** Guard */
	if (!incomingInvoiceId) return null;
	// A merged duplicate lives on inside its original, so its old address leads there.
	if (incomingInvoice?.state === "merged" && incomingInvoice.duplicateOf) {
		return (
			<Navigate
				to={`/controlling/incoming-invoices/${incomingInvoice.duplicateOf.id}`}
				replace
			/>
		);
	}

	/** Render */
	return (
		<ContentBox bottomPadding="none">
			<Can
				action="read"
				subject="IncomingInvoice"
				fallback={
					<BlankSlate
						Icon={icon("Lock")}
						label={t("general.no_access")}
					/>
				}
			>
				<IncomingInvoiceHeader incomingInvoiceId={incomingInvoiceId} />
				<Tabs
					panelKey="incoming-invoice-tabs"
					groups={[
						{
							name: "general",
							panels: [
								{
									label: t("general.general"),
									cyId: "incoming-invoice-general-tab",
									panel: () => <IncomingInvoiceGeneral incomingInvoiceId={incomingInvoiceId} />,
									errors: [],
								},
								{
									label: t("incoming_invoices.details"),
									cyId: "incoming-invoice-details-tab",
									panel: () => <IncomingInvoiceDetailsTab incomingInvoiceId={incomingInvoiceId} />,
									errors: [],
								},
								{
									label: t("incoming_invoices.positions"),
									cyId: "incoming-invoice-positions-tab",
									panel: () => <IncomingInvoicePositions incomingInvoiceId={incomingInvoiceId} />,
									errors: [],
								},
								{
									label: t("incoming_invoices.cost_allocation"),
									cyId: "incoming-invoice-cost-allocation-tab",
									panel: () => (
										<IncomingInvoiceCostAllocation incomingInvoiceId={incomingInvoiceId} />
									),
									errors: [],
								},
							],
						},
						{
							name: "review",
							panels: [
								{
									label: t("incoming_invoices.review"),
									cyId: "incoming-invoice-review-tab",
									panel: () => <IncomingInvoiceReview incomingInvoiceId={incomingInvoiceId} />,
									errors: [],
								},
							],
						},
						"spacer",
						{
							name: "admin",
							panels: [
								{
									label: t("settings.settings"),
									cyId: "incoming-invoice-settings-tab",
									panel: () => <IncomingInvoiceSettings incomingInvoiceId={incomingInvoiceId} />,
									errors: [],
								},
							],
						},
					]}
				/>
			</Can>
		</ContentBox>
	);
}
